import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import {
    BOT_AFTER_PROJECT_TYPE_DEAL,
    BOT_GREETING,
    BOT_ON_MSA_UPLOADED,
    BOT_ON_ORDER_FORM_UPLOADED,
    BOT_REPLIES_AFTER_CONTEXT,
    CONTEXT_FROM_ACTION,
    DEAL_CONTEXT_ITEMS,
    DEAL_SUGGESTED_DOCS,
    DPA_SUGGESTED_DOC,
    HEADER_FROM_ORDER_FORM,
    HEADER_SUMMARY,
    INITIAL_AVAILABLE_ACTIONS,
    INITIAL_CONTEXT,
    INITIAL_DOCUMENTS,
    MSA_DOC,
    MSA_FLAGS,
    NEXT_TALK_ACTION,
    ORDER_FORM_DOC,
    TRANSCRIPTS,
} from "./demoScript";
import type {
    ActionKey,
    ChatMessage,
    ContextId,
    DealHeader,
    DocItem,
    Flag,
    IntakeState,
    ProjectType,
} from "./types";

const INITIAL_HEADER: DealHeader = {
    dealName: null,
    vendor: null,
    tcv: null,
    isRenewal: null,
    startDate: null,
    endDate: null,
    summary: null,
};

const initialState = (): IntakeState => ({
    header: { ...INITIAL_HEADER },
    projectType: null,
    messages: [],
    documents: INITIAL_DOCUMENTS.map((d) => ({ ...d })),
    flags: [],
    contextItems: INITIAL_CONTEXT.map((c) => ({ ...c })),
    availableActions: [...INITIAL_AVAILABLE_ACTIONS],
    recordingTranscript: null,
    reviewOpen: false,
    submitted: false,
});

let messageCounter = 0;
const nextMessageId = () => `msg-${++messageCounter}`;

type Action =
    | { type: "APPEND_MESSAGE"; message: ChatMessage }
    | { type: "REPLACE_MESSAGE"; id: string; message: ChatMessage }
    | { type: "REMOVE_MESSAGE"; id: string }
    | { type: "SET_DOC"; id: string; patch: Partial<DocItem> }
    | { type: "ADD_DOC"; doc: DocItem }
    | { type: "REMOVE_DOC"; id: string }
    | { type: "ADD_FLAGS"; flags: Flag[] }
    | { type: "SET_HEADER"; patch: Partial<DealHeader> }
    | { type: "SET_PROJECT_TYPE"; projectType: ProjectType }
    | { type: "SET_CONTEXT_ITEMS"; items: typeof DEAL_CONTEXT_ITEMS }
    | { type: "MARK_CONTEXT_DONE"; id: ContextId }
    | { type: "SET_AVAILABLE_ACTIONS"; actions: ActionKey[] }
    | { type: "SET_RECORDING_TRANSCRIPT"; value: string | null }
    | { type: "OPEN_REVIEW" }
    | { type: "CLOSE_REVIEW" }
    | { type: "SUBMIT" }
    | { type: "RESET" };

function reducer(state: IntakeState, action: Action): IntakeState {
    switch (action.type) {
        case "APPEND_MESSAGE":
            return { ...state, messages: [...state.messages, action.message] };
        case "REPLACE_MESSAGE":
            return {
                ...state,
                messages: state.messages.map((m) => (m.id === action.id ? action.message : m)),
            };
        case "REMOVE_MESSAGE":
            return { ...state, messages: state.messages.filter((m) => m.id !== action.id) };
        case "SET_DOC":
            return {
                ...state,
                documents: state.documents.map((d) =>
                    d.id === action.id ? { ...d, ...action.patch } : d
                ),
            };
        case "ADD_DOC":
            if (state.documents.some((d) => d.id === action.doc.id)) return state;
            return { ...state, documents: [...state.documents, action.doc] };
        case "REMOVE_DOC":
            return { ...state, documents: state.documents.filter((d) => d.id !== action.id) };
        case "ADD_FLAGS":
            return { ...state, flags: [...state.flags, ...action.flags] };
        case "SET_HEADER":
            return { ...state, header: { ...state.header, ...action.patch } };
        case "SET_PROJECT_TYPE":
            return { ...state, projectType: action.projectType };
        case "SET_CONTEXT_ITEMS":
            // Idempotent: only seed the list when it's currently empty. Otherwise
            // a second reveal (e.g., order-form parse after talkProjectType)
            // would wipe out checks the user has already accumulated.
            if (state.contextItems.length > 0) return state;
            return { ...state, contextItems: action.items.map((c) => ({ ...c })) };
        case "MARK_CONTEXT_DONE":
            return {
                ...state,
                contextItems: state.contextItems.map((c) =>
                    c.id === action.id ? { ...c, status: "done" } : c
                ),
            };
        case "SET_AVAILABLE_ACTIONS":
            return { ...state, availableActions: action.actions };
        case "SET_RECORDING_TRANSCRIPT":
            return { ...state, recordingTranscript: action.value };
        case "OPEN_REVIEW":
            return { ...state, reviewOpen: true };
        case "CLOSE_REVIEW":
            return { ...state, reviewOpen: false };
        case "SUBMIT":
            return { ...state, submitted: true };
        case "RESET":
            messageCounter = 0;
            return initialState();
    }
}

type DocKey = "orderForm" | "msa";

export function useIntakeMachine() {
    const [state, dispatch] = useReducer(reducer, undefined, initialState);
    const timersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
    const intervalsRef = useRef<Set<ReturnType<typeof setInterval>>>(new Set());

    const setTrackedTimeout = useCallback((fn: () => void, ms: number) => {
        const id = setTimeout(() => {
            timersRef.current.delete(id);
            fn();
        }, ms);
        timersRef.current.add(id);
        return id;
    }, []);

    const setTrackedInterval = useCallback((fn: () => void, ms: number) => {
        const id = setInterval(fn, ms);
        intervalsRef.current.add(id);
        return id;
    }, []);

    const clearTrackedInterval = useCallback((id: ReturnType<typeof setInterval>) => {
        clearInterval(id);
        intervalsRef.current.delete(id);
    }, []);

    const clearAllTimers = useCallback(() => {
        timersRef.current.forEach((id) => clearTimeout(id));
        timersRef.current.clear();
        intervalsRef.current.forEach((id) => clearInterval(id));
        intervalsRef.current.clear();
    }, []);

    useEffect(() => {
        return () => {
            clearAllTimers();
        };
    }, [clearAllTimers]);

    // Greet on first mount.
    const greetedRef = useRef(false);
    useEffect(() => {
        if (greetedRef.current) return;
        greetedRef.current = true;
        dispatch({
            type: "APPEND_MESSAGE",
            message: {
                id: nextMessageId(),
                role: "bot",
                parts: [{ type: "html", content: BOT_GREETING }],
            },
        });
    }, []);

    // Builds a natural-language list of context topics still pending. Used to
    // turn generic "keep talking" prompts into concrete topic suggestions.
    const buildTopicSuggestion = useCallback((base: string): string => {
        const labels = state.contextItems
            .filter((c) => c.status === "pending")
            .map((c) => c.label.toLowerCase());
        if (labels.length === 0) return base;
        const list =
            labels.length === 1
                ? labels[0]
                : labels.length === 2
                  ? `${labels[0]} or ${labels[1]}`
                  : `${labels.slice(0, -1).join(", ")}, or ${labels[labels.length - 1]}`;
        return `${base} While I do, tell me about your ${list}.`;
    }, [state.contextItems]);

    /**
     * Append a bot message after a brief typing indicator. `parts` should be a
     * ReadyState-compliant ChatMessage["parts"].
     */
    const botReply = useCallback(
        (message: Omit<ChatMessage, "id" | "role" | "typing">) => {
            const typingId = nextMessageId();
            dispatch({
                type: "APPEND_MESSAGE",
                message: { id: typingId, role: "bot", typing: true, parts: [] },
            });
            setTrackedTimeout(() => {
                dispatch({
                    type: "REPLACE_MESSAGE",
                    id: typingId,
                    message: { ...message, id: typingId, role: "bot" },
                });
            }, 500);
        },
        [setTrackedTimeout]
    );

    const startDocLifecycle = useCallback(
        (key: DocKey) => {
            const def = key === "orderForm" ? ORDER_FORM_DOC : MSA_DOC;
            // Insert the doc into the checklist if it isn't there yet (it only
            // appears once the user uploads).
            // Ensure the canonical entry exists (it's normally seeded as
            // "suggested" via DEAL_SUGGESTED_DOCS) and swap its label to the
            // filename — we don't know the doc type until parsing finishes,
            // so the row shouldn't display "Order Form" / "MSA" yet.
            dispatch({
                type: "ADD_DOC",
                doc: {
                    id: def.id,
                    label: def.filename,
                    required: true,
                    state: "uploading",
                    filename: def.filename,
                    size: def.size,
                    progress: 0,
                },
            });
            dispatch({
                type: "SET_DOC",
                id: def.id,
                patch: {
                    state: "uploading",
                    label: def.filename,
                    filename: def.filename,
                    size: def.size,
                    progress: 0,
                    hint: undefined,
                    detectedType: undefined,
                },
            });

            const stepMs = 60;
            const totalSteps = Math.ceil(def.uploadMs / stepMs);
            let step = 0;
            const interval = setTrackedInterval(() => {
                step += 1;
                const progress = Math.min(100, (step / totalSteps) * 100);
                dispatch({ type: "SET_DOC", id: def.id, patch: { progress } });
                if (step >= totalSteps) {
                    clearTrackedInterval(interval);
                    dispatch({
                        type: "SET_DOC",
                        id: def.id,
                        patch: { state: "parsing", progress: 100 },
                    });
                    setTrackedTimeout(() => {
                        // Parsing complete — now we know the doc type, so set
                        // detectedType so the row can render the type tag.
                        dispatch({
                            type: "SET_DOC",
                            id: def.id,
                            patch: { state: "ready", detectedType: def.detectedType },
                        });
                        onDocReady(key);
                    }, def.parseMs);
                }
            }, stepMs);
        },
        [setTrackedInterval, clearTrackedInterval, setTrackedTimeout]
    );

    // Reveal the deal-specific context topics + suggested documents. Idempotent:
    // safe to call from either talkProjectType or from the order-form parse path.
    const revealDealScaffolding = useCallback(() => {
        dispatch({ type: "SET_PROJECT_TYPE", projectType: "deal" });
        dispatch({ type: "SET_CONTEXT_ITEMS", items: DEAL_CONTEXT_ITEMS });
        DEAL_SUGGESTED_DOCS.forEach((doc) => {
            dispatch({ type: "ADD_DOC", doc: { ...doc } });
        });
    }, []);

    const onDocReady = useCallback(
        (key: DocKey) => {
            if (key === "orderForm") {
                // Order form implies "this is a deal" — set up the scaffolding
                // if it hasn't already been set via talkProjectType.
                revealDealScaffolding();
                dispatch({ type: "SET_HEADER", patch: HEADER_FROM_ORDER_FORM });
                botReply({ parts: [{ type: "orderFormSummary" }] });
            } else {
                dispatch({ type: "ADD_DOC", doc: { ...DPA_SUGGESTED_DOC } });
                dispatch({ type: "ADD_FLAGS", flags: MSA_FLAGS });
                botReply({ parts: [{ type: "msaSummary" }] });
            }
        },
        [botReply, revealDealScaffolding]
    );

    const simulateVoice = useCallback(
        (transcript: string, onDone: () => void) => {
            // The view watches `state.recordingTranscript` to render the recording overlay
            // and the typed-out transcript inside the textarea.
            dispatch({ type: "SET_RECORDING_TRANSCRIPT", value: "" });

            setTrackedTimeout(() => {
                let i = 0;
                const stepMs = 22;
                const interval = setTrackedInterval(() => {
                    i += Math.max(3, Math.floor(Math.random() * 7));
                    const slice = transcript.slice(0, Math.min(i, transcript.length));
                    dispatch({ type: "SET_RECORDING_TRANSCRIPT", value: slice });
                    if (i >= transcript.length) {
                        clearTrackedInterval(interval);
                        setTrackedTimeout(() => {
                            dispatch({ type: "SET_RECORDING_TRANSCRIPT", value: null });
                            onDone();
                        }, 700);
                    }
                }, stepMs);
            }, 800);
        },
        [setTrackedInterval, clearTrackedInterval, setTrackedTimeout]
    );

    const runTalkAction = useCallback(
        (
            key: "talkCommercial" | "talkRisks" | "talkRelationship" | "talkAlternatives",
            mode: "typed" | "spoken" = "spoken"
        ) => {
            const transcript = TRANSCRIPTS[key];
            const contextId = CONTEXT_FROM_ACTION[key];
            // Remove this chip immediately so it can't be re-triggered mid-recording.
            dispatch({
                type: "SET_AVAILABLE_ACTIONS",
                actions: state.availableActions.filter((a) => a !== key),
            });
            const finish = () => {
                dispatch({
                    type: "APPEND_MESSAGE",
                    message: {
                        id: nextMessageId(),
                        role: "user",
                        parts: [{ type: "longtext", content: transcript }],
                    },
                });
                botReply({
                    parts: [{ type: "html", content: BOT_REPLIES_AFTER_CONTEXT[contextId] }],
                });
                dispatch({ type: "MARK_CONTEXT_DONE", id: contextId });
            };
            if (mode === "spoken") {
                simulateVoice(transcript, finish);
            } else {
                finish();
            }
        },
        [state.availableActions, simulateVoice, botReply]
    );

    const runAction = useCallback(
        (key: ActionKey, mode: "typed" | "spoken" = "spoken") => {
            switch (key) {
                case "uploadOrderForm": {
                    dispatch({
                        type: "APPEND_MESSAGE",
                        message: {
                            id: nextMessageId(),
                            role: "user",
                            parts: [
                                {
                                    type: "doc",
                                    filename: ORDER_FORM_DOC.filename,
                                    size: ORDER_FORM_DOC.size,
                                },
                            ],
                        },
                    });
                    dispatch({
                        type: "SET_AVAILABLE_ACTIONS",
                        actions: state.availableActions.filter((a) => a !== "uploadOrderForm"),
                    });
                    setTrackedTimeout(() => {
                        botReply({
                            parts: [
                                {
                                    type: "html",
                                    content: buildTopicSuggestion(BOT_ON_ORDER_FORM_UPLOADED),
                                },
                            ],
                        });
                        startDocLifecycle("orderForm");
                    }, 700);
                    break;
                }
                case "uploadMSA": {
                    dispatch({
                        type: "APPEND_MESSAGE",
                        message: {
                            id: nextMessageId(),
                            role: "user",
                            parts: [
                                {
                                    type: "doc",
                                    filename: MSA_DOC.filename,
                                    size: MSA_DOC.size,
                                },
                            ],
                        },
                    });
                    dispatch({
                        type: "SET_AVAILABLE_ACTIONS",
                        actions: state.availableActions.filter((a) => a !== "uploadMSA"),
                    });
                    setTrackedTimeout(() => {
                        botReply({
                            parts: [
                                {
                                    type: "html",
                                    content: buildTopicSuggestion(BOT_ON_MSA_UPLOADED),
                                },
                            ],
                        });
                        startDocLifecycle("msa");
                    }, 700);
                    break;
                }
                case "talkProjectType": {
                    const transcript = TRANSCRIPTS.talkProjectType;
                    dispatch({
                        type: "SET_AVAILABLE_ACTIONS",
                        actions: state.availableActions.filter((a) => a !== "talkProjectType"),
                    });
                    const finish = () => {
                        dispatch({
                            type: "APPEND_MESSAGE",
                            message: {
                                id: nextMessageId(),
                                role: "user",
                                parts: [{ type: "longtext", content: transcript }],
                            },
                        });
                        revealDealScaffolding();
                        // The user named the agreement ("AWS Private Pricing
                        // Addendum") — seed the project name from that.
                        dispatch({
                            type: "SET_HEADER",
                            patch: { dealName: HEADER_FROM_ORDER_FORM.dealName },
                        });
                        botReply({
                            parts: [{ type: "html", content: BOT_AFTER_PROJECT_TYPE_DEAL }],
                        });
                    };
                    if (mode === "spoken") {
                        simulateVoice(transcript, finish);
                    } else {
                        finish();
                    }
                    break;
                }
                case "talkCommercial":
                case "talkRisks":
                case "talkRelationship":
                case "talkAlternatives":
                    runTalkAction(key, mode);
                    break;
                case "review":
                    dispatch({ type: "OPEN_REVIEW" });
                    break;
            }
        },
        [
            state.availableActions,
            setTrackedTimeout,
            botReply,
            buildTopicSuggestion,
            startDocLifecycle,
            runTalkAction,
            simulateVoice,
            revealDealScaffolding,
        ]
    );

    // Refresh available actions whenever doc states or context completion changes.
    // This effect computes the next available chips deterministically from state,
    // rather than asking each handler to maintain it.
    useEffect(() => {
        const next = new Set<ActionKey>(state.availableActions);

        const projectTypeKnown = state.projectType !== null;

        // Project-type discovery: available until we know the type.
        if (!projectTypeKnown) next.add("talkProjectType");

        // Docs are added to the list only once the user uploads. "Unsubmitted"
        // means either absent or still in a `suggested` placeholder state.
        const orderFormDoc = state.documents.find((d) => d.id === "orderForm");
        const msaDoc = state.documents.find((d) => d.id === "msa");
        const orderFormReady = orderFormDoc?.state === "ready";
        const orderFormUnsubmitted = !orderFormDoc || orderFormDoc.state === "suggested";
        const msaUnsubmitted = !msaDoc || msaDoc.state === "suggested";
        if (orderFormReady && msaUnsubmitted) next.add("uploadMSA");
        if (orderFormUnsubmitted) next.add("uploadOrderForm");

        // Context talk actions only unlock once we know it's a deal project.
        const doneContexts = new Set(
            state.contextItems.filter((c) => c.status === "done").map((c) => c.id)
        );
        if (projectTypeKnown && !doneContexts.has("commercial")) {
            next.add("talkCommercial");
        }
        if (doneContexts.has("commercial") && NEXT_TALK_ACTION.talkCommercial) {
            next.add(NEXT_TALK_ACTION.talkCommercial);
        }
        if (doneContexts.has("risks") && NEXT_TALK_ACTION.talkRisks) {
            next.add(NEXT_TALK_ACTION.talkRisks);
        }
        if (doneContexts.has("relationship") && NEXT_TALK_ACTION.talkRelationship) {
            next.add(NEXT_TALK_ACTION.talkRelationship);
        }

        // Once all 4 contexts are done, populate the summary and unlock review
        // (if both required docs are also ready).
        const allContextsDone =
            state.contextItems.length > 0 &&
            state.contextItems.every((c) => c.status === "done");
        const allRequiredDocsReady =
            orderFormDoc?.state === "ready" && msaDoc?.state === "ready";
        if (allContextsDone && !state.header.summary) {
            dispatch({ type: "SET_HEADER", patch: { summary: HEADER_SUMMARY } });
        }
        if (allContextsDone && allRequiredDocsReady) {
            next.add("review");
        }

        // Remove actions that have already been consumed / are no longer valid.
        const filtered = Array.from(next).filter((a) => {
            if (a === "talkProjectType") return !projectTypeKnown;
            if (a === "uploadOrderForm") return orderFormUnsubmitted;
            if (a === "uploadMSA") return msaUnsubmitted;
            if (a === "talkCommercial") return projectTypeKnown && !doneContexts.has("commercial");
            if (a === "talkRisks") return !doneContexts.has("risks") && doneContexts.has("commercial");
            if (a === "talkRelationship")
                return !doneContexts.has("relationship") && doneContexts.has("risks");
            if (a === "talkAlternatives")
                return !doneContexts.has("alternatives") && doneContexts.has("relationship");
            if (a === "review") return allContextsDone && allRequiredDocsReady;
            return true;
        });

        // Only dispatch when the set actually changed (avoid render loops).
        const sortedNew = filtered.slice().sort();
        const sortedCur = state.availableActions.slice().sort();
        if (
            sortedNew.length !== sortedCur.length ||
            sortedNew.some((a, i) => a !== sortedCur[i])
        ) {
            dispatch({ type: "SET_AVAILABLE_ACTIONS", actions: filtered });
        }
    }, [
        state.documents,
        state.contextItems,
        state.availableActions,
        state.header.summary,
        state.projectType,
    ]);

    const reset = useCallback(() => {
        clearAllTimers();
        greetedRef.current = false;
        dispatch({ type: "RESET" });
    }, [clearAllTimers]);

    const submit = useCallback(() => {
        dispatch({ type: "SUBMIT" });
    }, []);

    const closeReview = useCallback(() => {
        dispatch({ type: "CLOSE_REVIEW" });
    }, []);

    // Helper for the view: find the primary upload/talk chip when the
    // attach/mic icon button is clicked.
    const firstActionByPrefix = useCallback(
        (prefix: "upload" | "talk"): ActionKey | undefined => {
            return state.availableActions.find((a) => a.startsWith(prefix));
        },
        [state.availableActions]
    );

    const reviewReady = useMemo(() => {
        const allContextsDone = state.contextItems.every((c) => c.status === "done");
        const orderFormReady =
            state.documents.find((d) => d.id === "orderForm")?.state === "ready";
        const msaReady = state.documents.find((d) => d.id === "msa")?.state === "ready";
        return allContextsDone && orderFormReady && msaReady;
    }, [state.contextItems, state.documents]);

    return {
        state,
        runAction,
        reset,
        submit,
        closeReview,
        firstActionByPrefix,
        reviewReady,
    };
}

export type IntakeMachine = ReturnType<typeof useIntakeMachine>;
